import {
  CREATE,
  DELETE,
  FIND,
  GET,
  GET_BY_ORGANIZATION_ID,
  GET_PROJECT_BENEFICIARY_USERS,
  GET_PROJECT_TEAM_MEMBER_USERS,
  GET_PROJECT_TENANT_USERS,
  UPDATE,
} from './constants';
import {
  FIND as FIND_ORGANIZATION,
  GET_BY_TYPE as GET_ORGANIZATIONS_BY_TYPE,
} from '../realm/constants';
import { STATUS } from '../../utils/constants';
import { buildQueryKey } from '../../utils/helpers';
import {
  addToDictionary,
  addToList,
  changeListStatus,
  hasPageSizeChanged,
  resetState,
} from '../helpers';
import UserModel from '../../api/models/UserModel';

/**
 *
 * @type {{list: {default: {}, pagination: {total: number, pageNumber: null, pageSize: null}, meta: {currentKey: string}}, dictionary: {}}}
 */
const initialState = {
  dictionary: {},
  list: {
    meta: {
      currentKey: 'default',
    },
    pagination: {
      pageNumber: null,
      pageSize: null,
      total: 0,
    },
    default: {},
  },
};

export default function userReducer(state = initialState, action) {
  let queryKey;
  let listStatus;
  let error;
  let id;
  let resourceModel;
  let resetToDefault;
  let resources;
  let pager;

  switch (action.type) {
    case GET.REQUEST:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;
      resetToDefault = action.payload.resetToDefault;

      if (
        hasPageSizeChanged(state.list.pagination.pageSize, pager?.pageSize) ||
        resetToDefault
      ) {
        return resetState(state, initialState, {
          queryKey,
          pager,
        });
      }

      if (
        [STATUS.RELOADING, STATUS.SUCCESS, STATUS.CACHED].includes(listStatus)
      ) {
        return changeListStatus(state, { pager, queryKey }, STATUS.RELOADING);
      }
      return changeListStatus(state, { pager, queryKey }, STATUS.LOADING);
    case GET.SUCCESS:
      resourceModel = action.payload.resourceModel;
      resources = action.payload.resources;

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, resourceModel),
        list: addToList(state.list, { ...action.payload, resources }),
      };
    case GET.ERROR:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      error = action.payload.error;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;

      return changeListStatus(
        state,
        { pager, queryKey, error },
        listStatus === STATUS.RELOADING ? STATUS.CACHED : STATUS.ERROR
      );

    case GET_BY_ORGANIZATION_ID.REQUEST:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;
      resetToDefault = action.payload.resetToDefault;

      if (
        hasPageSizeChanged(state.list.pagination.pageSize, pager?.pageSize) ||
        resetToDefault
      ) {
        return resetState(state, initialState, {
          queryKey,
          pager,
        });
      }

      if (
        [STATUS.RELOADING, STATUS.SUCCESS, STATUS.CACHED].includes(listStatus)
      ) {
        return changeListStatus(state, { pager, queryKey }, STATUS.RELOADING);
      }
      return changeListStatus(state, { pager, queryKey }, STATUS.LOADING);
    case GET_BY_ORGANIZATION_ID.SUCCESS:
      resourceModel = action.payload.resourceModel;
      resources = action.payload.resources;
      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, resourceModel),
        list: addToList(state.list, { ...action.payload, resources }),
      };
    case GET_BY_ORGANIZATION_ID.ERROR:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      error = action.payload.error;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;

      return changeListStatus(
        state,
        { pager, queryKey, error },
        listStatus === STATUS.RELOADING ? STATUS.CACHED : STATUS.ERROR
      );

    case FIND.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case FIND.SUCCESS:
      const { resource } = action.payload;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [resource.id]: resource,
        },
      };
    case FIND.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case CREATE.REQUEST:
      return state;
    case CREATE.SUCCESS:
      return state;
    case CREATE.ERROR:
      return state;

    case UPDATE.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case UPDATE.SUCCESS:
      return state;
    case UPDATE.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case DELETE.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case DELETE.SUCCESS:
      return state;
    case DELETE.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case GET_PROJECT_TEAM_MEMBER_USERS.REQUEST:
      return state;
    case GET_PROJECT_TEAM_MEMBER_USERS.SUCCESS:
      resourceModel = action.payload.resourceModel;
      resources = action.payload.resources[0]?.users ?? [];

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, resourceModel),
      };
    case GET_PROJECT_TEAM_MEMBER_USERS.ERROR:
      return state;

    case GET_PROJECT_TENANT_USERS.REQUEST:
      return state;
    case GET_PROJECT_TENANT_USERS.SUCCESS:
      resourceModel = action.payload.userModel;
      resources = action.payload.resources.reduce(
        (accumulator, organization) => {
          return [...accumulator, ...organization.users];
        },
        []
      );

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, resourceModel),
      };
    case GET_PROJECT_TENANT_USERS.ERROR:
      return state;

    case GET_PROJECT_BENEFICIARY_USERS.REQUEST:
      return state;
    case GET_PROJECT_BENEFICIARY_USERS.SUCCESS:
      resourceModel = action.payload.userModel;
      resources = action.payload.resources.reduce(
        (accumulator, organization) => {
          return [...accumulator, ...organization.users];
        },
        []
      );

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, resourceModel),
      };
    case GET_PROJECT_BENEFICIARY_USERS.ERROR:
      return state;

    case FIND_ORGANIZATION.SUCCESS:
      resources = action.payload.resource.users ?? [];

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, UserModel),
      };

    case GET_ORGANIZATIONS_BY_TYPE.SUCCESS:
      resources = action.payload.resources.reduce(
        (accumulator, organization) => {
          return [
            ...accumulator,
            ...(organization?.users ? organization?.users : []),
          ];
        },
        []
      );

      return {
        ...state,
        dictionary: addToDictionary(state.dictionary, resources, UserModel),
      };

    default:
      return state;
  }
}
